/**
 * High-Priority Subscription Feature Components
 *
 * Three self-contained, enterprise-grade UI components:
 *   BillingContactCard — Billing email, CC list, company info, VAT/Tax, address
 *   CostCenterCard     — Cost center, PO number, department, fiscal year, budget code
 *   SSOConfigTab       — Full SAML SSO setup wizard (Tab 7 / Enterprise-gated)
 */

import React, { useState, useEffect } from "react";
import {
  Mail, Building2, Tag, Globe, Phone, MapPin, Plus, X,
  Lock, CheckCircle2, AlertTriangle, Loader2, ExternalLink,
  Copy, ChevronDown, ShieldCheck, ShieldX, Zap, Crown,
  BadgeCheck, KeyRound, Link2, RefreshCw, Eye, EyeOff,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../shadcn/card";
import { Button } from "../shadcn/button";
import { Badge } from "../shadcn/badge";
import { Separator } from "../shadcn/separator";
import { Skeleton } from "../shadcn/skeleton";

import {
  getBillingContact, saveBillingContact,
  getBillingMetadata, saveBillingMetadata,
  getSSOConfig, saveSSOConfig, testSSOConfig,
  type BillingContact, type BillingMetadata, type SSOConfig,
} from "../../api/subscriptions";
import { cn } from "../../lib/utils";

// ── Shared helpers ────────────────────────────────────────────────────────────

function SaveButton({ pending, saved }: { pending: boolean; saved: boolean }) {
  return (
    <Button size="sm" type="submit" className="w-full gap-2">
      {pending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {saved
        ? <><CheckCircle2 className="h-3.5 w-3.5 text-green-400" /> Saved</>
        : "Save Changes"}
    </Button>
  );
}

function FieldRow({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="grid grid-cols-[140px_1fr] items-start gap-3">
      <div className="pt-2">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide leading-none">
          {label}
        </label>
        {hint && <p className="text-[10px] text-muted-foreground/70 mt-0.5 leading-snug">{hint}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function Input({
  value,
  onChange,
  placeholder,
  type = "text",
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  className?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={cn(
        "w-full text-sm rounded-lg border border-border/60 bg-secondary/40",
        "px-3 py-2 text-foreground placeholder:text-muted-foreground",
        "focus:outline-none focus:ring-1 focus:ring-emerald-500 transition-colors",
        className
      )}
    />
  );
}

// ── 1. BillingContactCard ─────────────────────────────────────────────────────

const DEFAULT_CONTACT: BillingContact = {
  billing_email: "",
  billing_cc: [],
  company_name: "",
  vat_number: "",
  tax_id: "",
  phone: "",
  address_line1: "",
  address_city: "",
  address_country: "",
};

export function BillingContactCard() {
  const qc = useQueryClient();
  const contactQuery = useQuery({
    queryKey: ["subscriptions", "billing-contact"],
    queryFn:  getBillingContact,
    select:   (d) => d.contact,
  });

  const [draft, setDraft] = useState<BillingContact>(DEFAULT_CONTACT);
  const [ccInput, setCcInput] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (contactQuery.data) setDraft(contactQuery.data);
  }, [contactQuery.data]);

  const mutation = useMutation({
    mutationFn: () => saveBillingContact(draft),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      qc.invalidateQueries({ queryKey: ["subscriptions", "billing-contact"] });
      qc.invalidateQueries({ queryKey: ["subscriptions", "overview"] });
    },
  });

  const set = (field: keyof BillingContact, value: unknown) =>
    setDraft((p) => ({ ...p, [field]: value }));

  const addCc = () => {
    const email = ccInput.trim().toLowerCase();
    if (!email || draft.billing_cc.includes(email)) return;
    set("billing_cc", [...draft.billing_cc, email]);
    setCcInput("");
  };

  const removeCc = (e: string) =>
    set("billing_cc", draft.billing_cc.filter((x) => x !== e));

  if (contactQuery.isLoading) {
    return <Card><CardContent className="p-6"><Skeleton className="h-48 w-full" /></CardContent></Card>;
  }

  return (
    <Card className="overflow-hidden">
      <div className="h-0.5 w-full bg-gradient-to-r from-sky-500/60 to-emerald-500/60" />
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" />
          Billing Contact
        </CardTitle>
        <CardDescription className="text-xs">
          Invoice recipient details — separate from your login email and synced to Paystack.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form
          onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}
          className="space-y-4"
        >
          {/* Primary billing email */}
          <FieldRow label="Billing Email" hint="Receives all invoices">
            <Input
              type="email"
              value={draft.billing_email}
              onChange={(v) => set("billing_email", v)}
              placeholder="billing@company.com"
            />
          </FieldRow>

          {/* CC emails */}
          <FieldRow label="CC Recipients" hint="Additional invoice recipients">
            <div className="space-y-2">
              {draft.billing_cc.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {draft.billing_cc.map((e) => (
                    <span
                      key={e}
                      className="inline-flex items-center gap-1 text-[11px] font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 px-2 py-0.5 rounded-full"
                    >
                      {e}
                      <button type="button" onClick={() => removeCc(e)} className="hover:text-red-500 transition-colors">
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="email"
                  value={ccInput}
                  onChange={(e) => setCcInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCc(); } }}
                  placeholder="cc@company.com"
                  className={cn(
                    "flex-1 text-sm rounded-lg border border-border/60 bg-secondary/40",
                    "px-3 py-2 text-foreground placeholder:text-muted-foreground",
                    "focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  )}
                />
                <Button type="button" variant="outline" size="sm" onClick={addCc} className="gap-1">
                  <Plus className="h-3.5 w-3.5" /> Add
                </Button>
              </div>
            </div>
          </FieldRow>

          <Separator className="bg-border/40" />

          {/* Company details */}
          <FieldRow label="Company Name">
            <Input
              value={draft.company_name}
              onChange={(v) => set("company_name", v)}
              placeholder="Acme Corp Ltd."
            />
          </FieldRow>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-[152px]">
            <div>
              <label className="text-[11px] text-muted-foreground font-semibold uppercase mb-1 block">VAT Number</label>
              <Input
                value={draft.vat_number}
                onChange={(v) => set("vat_number", v)}
                placeholder="GB123456789"
              />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground font-semibold uppercase mb-1 block">Tax ID / EIN</label>
              <Input
                value={draft.tax_id}
                onChange={(v) => set("tax_id", v)}
                placeholder="12-3456789"
              />
            </div>
          </div>

          <FieldRow label="Phone">
            <Input
              type="tel"
              value={draft.phone}
              onChange={(v) => set("phone", v)}
              placeholder="+1 (555) 000-0000"
            />
          </FieldRow>

          <Separator className="bg-border/40" />

          {/* Address */}
          <FieldRow label="Address">
            <div className="space-y-2">
              <Input
                value={draft.address_line1}
                onChange={(v) => set("address_line1", v)}
                placeholder="123 Main Street, Suite 400"
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={draft.address_city}
                  onChange={(v) => set("address_city", v)}
                  placeholder="City"
                />
                <Input
                  value={draft.address_country}
                  onChange={(v) => set("address_country", v)}
                  placeholder="Country"
                />
              </div>
            </div>
          </FieldRow>

          <SaveButton pending={mutation.isPending} saved={saved} />
        </form>
      </CardContent>
    </Card>
  );
}

// ── 2. CostCenterCard ─────────────────────────────────────────────────────────

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DEFAULT_META: BillingMetadata = {
  cost_center: "",
  po_number: "",
  internal_reference: "",
  fiscal_year_start: 1,
  department: "",
  budget_code: "",
};

export function CostCenterCard() {
  const qc = useQueryClient();
  const metaQuery = useQuery({
    queryKey: ["subscriptions", "billing-metadata"],
    queryFn:  getBillingMetadata,
    select:   (d) => d.metadata,
  });

  const [draft, setDraft] = useState<BillingMetadata>(DEFAULT_META);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (metaQuery.data) setDraft(metaQuery.data);
  }, [metaQuery.data]);

  const mutation = useMutation({
    mutationFn: () => saveBillingMetadata(draft),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      qc.invalidateQueries({ queryKey: ["subscriptions", "billing-metadata"] });
    },
  });

  const set = (field: keyof BillingMetadata, value: unknown) =>
    setDraft((p) => ({ ...p, [field]: value }));

  if (metaQuery.isLoading) {
    return <Card><CardContent className="p-6"><Skeleton className="h-32 w-full" /></CardContent></Card>;
  }

  return (
    <Card className="overflow-hidden">
      <div className="h-0.5 w-full bg-gradient-to-r from-amber-500/60 to-orange-500/60" />
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          Internal Billing Info
        </CardTitle>
        <CardDescription className="text-xs">
          Cost center, PO number, and internal accounting fields. Printed on invoices for finance teams.
        </CardDescription>
      </CardHeader>

      <CardContent>
        <form
          onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { label: "Cost Center",        key: "cost_center",        placeholder: "CC-12345"        },
              { label: "PO Number",          key: "po_number",          placeholder: "PO-2026-001"     },
              { label: "Internal Reference", key: "internal_reference", placeholder: "ENG-PAXLEY"      },
              { label: "Department",         key: "department",         placeholder: "Engineering"     },
              { label: "Budget Code",        key: "budget_code",        placeholder: "BUD-2026-IT"     },
            ].map(({ label, key, placeholder }) => (
              <div key={key}>
                <label className="text-[11px] text-muted-foreground font-semibold uppercase mb-1.5 block">
                  {label}
                </label>
                <Input
                  value={String((draft as unknown as Record<string, unknown>)[key] ?? "")}
                  onChange={(v) => set(key as keyof BillingMetadata, v)}
                  placeholder={placeholder}
                />
              </div>
            ))}

            {/* Fiscal year start */}
            <div>
              <label className="text-[11px] text-muted-foreground font-semibold uppercase mb-1.5 block">
                Fiscal Year Start
              </label>
              <div className="relative">
                <select
                  value={draft.fiscal_year_start}
                  onChange={(e) => set("fiscal_year_start", Number(e.target.value))}
                  className={cn(
                    "w-full text-sm rounded-lg border border-border/60 bg-secondary/40",
                    "px-3 py-2 text-foreground focus:outline-none focus:ring-1 focus:ring-emerald-500",
                    "appearance-none pr-8"
                  )}
                >
                  {MONTHS.map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Preview — how it appears on invoice */}
          {(draft.cost_center || draft.po_number) && (
            <div className="rounded-xl border border-dashed border-border/60 bg-secondary/20 p-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Invoice memo preview
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed font-mono">
                {[
                  draft.cost_center && `CC: ${draft.cost_center}`,
                  draft.po_number && `PO: ${draft.po_number}`,
                  draft.department && `Dept: ${draft.department}`,
                  draft.budget_code && `Bud: ${draft.budget_code}`,
                ].filter(Boolean).join("  ")}
              </p>
            </div>
          )}

          <SaveButton pending={mutation.isPending} saved={saved} />
        </form>
      </CardContent>
    </Card>
  );
}

// ── 3. SSOConfigTab (Tab 7 — Enterprise gate) ─────────────────────────────────

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={copy}
      className="flex-shrink-0 p-1.5 rounded-md hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
      title="Copy to clipboard"
    >
      {copied
        ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
        : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function SPDetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-border/40 last:border-0">
      <div className="min-w-0">
        <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
        <p className="text-xs font-mono text-foreground mt-0.5 break-all">{value}</p>
      </div>
      <CopyButton text={value} />
    </div>
  );
}

/**
 * SAML SSO is not yet terminated by this deployment: the SP URLs below are the
 * addresses the endpoints will use, and until the handler ships they resolve to
 * the sign-in page. Saying so beats letting an admin paste them into Okta and
 * find out during a live cutover.
 */
function SamlNotLiveNotice({ available }: { available?: boolean }) {
  if (available) return null;
  return (
    <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
      <div className="text-xs text-muted-foreground">
        <span className="font-semibold text-foreground">Not live yet.</span>{" "}
        SAML sign-in is not enabled on this deployment, so these endpoints do not yet
        accept assertions. Save your IdP details here and contact us to schedule
        provisioning — we'll confirm before you point your IdP at them.
      </div>
    </div>
  );
}

interface SSOConfigTabProps {
  onUpgradeRequired?: (plan: "enterprise") => void;
}

export function SSOConfigTab({ onUpgradeRequired }: SSOConfigTabProps) {
  const qc = useQueryClient();
  const ssoQuery = useQuery({
    queryKey: ["subscriptions", "sso-config"],
    queryFn:  getSSOConfig,
    retry:    false,    // don't retry 402 errors
  });

  const [draft, setDraft] = useState<{
    idp_metadata_url: string;
    idp_entity_id: string;
    enabled: boolean;
  }>({ idp_metadata_url: "", idp_entity_id: "", enabled: false });

  const [testState, setTestState] = useState<{
    loading: boolean;
    result: { reachable: boolean; status_code: number | null; error: string | null } | null;
  }>({ loading: false, result: null });

  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (ssoQuery.data?.config) {
      const c = ssoQuery.data.config;
      setDraft({
        idp_metadata_url: c.idp_metadata_url ?? "",
        idp_entity_id:    c.idp_entity_id    ?? "",
        enabled:          c.enabled,
      });
    }
  }, [ssoQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () => saveSSOConfig({
      idp_metadata_url: draft.idp_metadata_url || undefined,
      idp_entity_id:    draft.idp_entity_id    || undefined,
      enabled:          draft.enabled,
    }),
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      qc.invalidateQueries({ queryKey: ["subscriptions", "sso-config"] });
    },
  });

  const handleTest = async () => {
    if (!draft.idp_metadata_url) return;
    setTestState({ loading: true, result: null });
    try {
      const res = await testSSOConfig(draft.idp_metadata_url);
      setTestState({ loading: false, result: res });
      qc.invalidateQueries({ queryKey: ["subscriptions", "sso-config"] });
    } catch {
      setTestState({ loading: false, result: { reachable: false, status_code: null, error: "Request failed" } });
    }
  };

  // Loading skeleton
  if (ssoQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Card><CardContent className="p-6"><Skeleton className="h-32 w-full" /></CardContent></Card>
        <Card><CardContent className="p-6"><Skeleton className="h-48 w-full" /></CardContent></Card>
      </div>
    );
  }

  // 402 — Enterprise gate
  const is402 = (ssoQuery.error as any)?.status === 402 || ssoQuery.data?.plan !== "enterprise";
  const sp    = ssoQuery.data?.sp;

  if (is402 || !ssoQuery.data || ssoQuery.data.plan !== "enterprise") {
    return (
      <div className="space-y-6">
        {/* Preview SP details anyway — so admins can see what they'd get */}
        {sp && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Link2 className="h-4 w-4 text-muted-foreground" />
                Your Service Provider Details
              </CardTitle>
              <CardDescription className="text-xs">
                The addresses your IdP would use once SSO is provisioned for your organization.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <SamlNotLiveNotice available={sp.available} />
              <SPDetailRow label="Entity ID (SP)"   value={sp.entity_id} />
              <SPDetailRow label="ACS URL"           value={sp.acs_url} />
              <SPDetailRow label="SLO URL"           value={sp.slo_url} />
            </CardContent>
          </Card>
        )}

        {/* Enterprise upgrade prompt */}
        <Card className="border-amber-500/30 bg-amber-500/5 overflow-hidden">
          <div className="h-0.5 w-full bg-gradient-to-r from-amber-400 to-orange-400" />
          <CardContent className="p-8 text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-amber-500/10 mb-4 mx-auto">
              <Crown className="h-7 w-7 text-amber-500" />
            </div>
            <h3 className="text-base font-bold text-foreground">SAML SSO — Enterprise Feature</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              Single Sign-On lets your team authenticate with Okta, Azure AD, Google Workspace, or any
              SAML 2.0 provider. Required for most enterprise security policies.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6 text-left max-w-lg mx-auto">
              {[
                "Okta & Azure AD support",
                "Google Workspace SAML",
                "Auth0 & OneLogin",
                "SCIM provisioning",
                "Just-in-time provisioning",
                "Centralized access control",
              ].map((f) => (
                <div key={f} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                  {f}
                </div>
              ))}
            </div>
            <Button
              className="mt-6 gap-2 bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => onUpgradeRequired?.("enterprise")}
            >
              <Zap className="h-4 w-4" /> Upgrade to Enterprise
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const config   = ssoQuery.data.config;
  const lastTest = config.last_tested_at ? new Date(config.last_tested_at).toLocaleString() : null;

  return (
    <div className="space-y-6">
      {/* SP Details */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Link2 className="h-4 w-4 text-muted-foreground" />
              Your Service Provider Details
            </CardTitle>
            <Badge variant="outline" className="text-[10px] text-amber-500 border-amber-500/40 bg-amber-500/10">
              <Crown className="h-2.5 w-2.5 mr-1" /> Enterprise
            </Badge>
          </div>
          <CardDescription className="text-xs">
            The endpoints your IdP (Okta, Azure AD, Google Workspace, Auth0) will use.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SamlNotLiveNotice available={sp!.available} />
          <SPDetailRow label="Entity ID (SP)"   value={sp!.entity_id} />
          <SPDetailRow label="ACS URL (Reply)"  value={sp!.acs_url}   />
          <SPDetailRow label="SLO URL (Logout)" value={sp!.slo_url}   />
        </CardContent>
      </Card>

      {/* IdP Configuration Form */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            IdP Configuration
          </CardTitle>
          <CardDescription className="text-xs">
            Paste your Identity Provider's metadata URL or Entity ID below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }}
            className="space-y-5"
          >
            {/* IdP Metadata URL */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">
                IdP Metadata URL
              </label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={draft.idp_metadata_url}
                  onChange={(e) => setDraft((p) => ({ ...p, idp_metadata_url: e.target.value }))}
                  placeholder="https://okta.com/app/xxx/sso/saml/metadata"
                  className={cn(
                    "flex-1 text-sm rounded-lg border border-border/60 bg-secondary/40",
                    "px-3 py-2 text-foreground placeholder:text-muted-foreground",
                    "focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  )}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleTest}
                  disabled={!draft.idp_metadata_url || testState.loading}
                  className="gap-1.5 flex-shrink-0"
                >
                  {testState.loading
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <RefreshCw className="h-3.5 w-3.5" />}
                  Test
                </Button>
              </div>

              {/* Test result */}
              {testState.result && (
                <div className={cn(
                  "mt-2 flex items-center gap-2 text-xs rounded-lg px-3 py-2",
                  testState.result.reachable
                    ? "bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20"
                    : "bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20"
                )}>
                  {testState.result.reachable
                    ? <ShieldCheck className="h-4 w-4 flex-shrink-0" />
                    : <ShieldX className="h-4 w-4 flex-shrink-0" />}
                  <span>
                    {testState.result.reachable
                      ? `Connection successful · HTTP ${testState.result.status_code}`
                      : `Connection failed · ${testState.result.error ?? "Unknown error"}`}
                  </span>
                </div>
              )}

              {/* Previous test state */}
              {!testState.result && config.test_status && lastTest && (
                <p className={cn("mt-1.5 text-[11px] flex items-center gap-1",
                  config.test_status === "success" ? "text-green-500" : "text-muted-foreground"
                )}>
                  {config.test_status === "success"
                    ? <BadgeCheck className="h-3 w-3" />
                    : <AlertTriangle className="h-3 w-3" />}
                  Last tested: {lastTest} — {config.test_status}
                  {config.test_error && ` · ${config.test_error}`}
                </p>
              )}
            </div>

            {/* IdP Entity ID */}
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">
                IdP Entity ID <span className="text-muted-foreground font-normal normal-case">(optional — usually from metadata)</span>
              </label>
              <input
                type="text"
                value={draft.idp_entity_id}
                onChange={(e) => setDraft((p) => ({ ...p, idp_entity_id: e.target.value }))}
                placeholder="https://accounts.google.com/o/saml2?idpid=xxx"
                className={cn(
                  "w-full text-sm rounded-lg border border-border/60 bg-secondary/40",
                  "px-3 py-2 text-foreground placeholder:text-muted-foreground",
                  "focus:outline-none focus:ring-1 focus:ring-emerald-500"
                )}
              />
            </div>

            {/* Enable/Disable SSO */}
            <div className="flex items-center justify-between rounded-xl border border-border/60 bg-secondary/40 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-foreground">Enable SAML SSO</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {draft.enabled
                    ? "Active — users are redirected to your IdP to sign in"
                    : "Inactive — users sign in with GitHub OAuth"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDraft((p) => ({ ...p, enabled: !p.enabled }))}
                className={cn(
                  "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent",
                  "transition-colors duration-200 ease-in-out focus:outline-none",
                  draft.enabled ? "bg-green-500" : "bg-secondary border border-border"
                )}
              >
                <span
                  className={cn(
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow",
                    "transition duration-200 ease-in-out",
                    draft.enabled ? "translate-x-5" : "translate-x-0"
                  )}
                />
              </button>
            </div>

            {/* Warning when enabling SSO without testing */}
            {draft.enabled && !testState.result?.reachable && config.test_status !== "success" && (
              <div className="flex items-start gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded-lg px-3 py-2.5 border border-amber-500/20">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Test your IdP metadata URL before enabling SSO. If the connection fails,
                  users may be locked out.
                </span>
              </div>
            )}

            <SaveButton pending={saveMutation.isPending} saved={saved} />
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
